import {
  FirmData,
  GroupListRequest,
} from '../../../apis/firm-management/firm-management-api.def.ts'
import { ColDef, GridOptions } from 'ag-grid-community'
import { useTranslation } from 'react-i18next'
import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import GroupDetailRenderer from './group-detail-renderer'
import { StateCell } from './state-cell.tsx'
import { UserButtonCell } from './user-button-cell.tsx'
import { CustomCellRendererProps } from 'ag-grid-react'
import { IdeyaButton } from '../../../common/antd/ideya-button/ideya-button.tsx'
import { EditOutlined } from '@ant-design/icons'
import { useMemo } from 'react'
import { useIsSuperViewer } from '../../../common/hooks/use-is-superviewer.ts'
import { useIsMobile } from '../../../common/hooks/use-is-mobile.ts'

type GroupQueryBase = Omit<GroupListRequest, 'firmId'>

interface FirmManagementTableProps {
  firmList: FirmData[]
  isLoading: boolean
  isFetching: boolean
  createGroupClicked: (firmId: string) => void
  onEditFirm: (id: string) => void
  groupQueryBase?: GroupQueryBase // e.g. { status: 'all', page: 1 }
  height: number
  onEditGroup: (id: string, firmId: string) => void
}

export const FirmManagementTable: React.FC<FirmManagementTableProps> = ({
  firmList,
  isLoading,
  isFetching,
  createGroupClicked,
  onEditFirm,
  groupQueryBase = { status: 'all', page: 1 },
  height,
  onEditGroup,
}) => {
  const { t } = useTranslation()
  const isSuperViewer = useIsSuperViewer()
  const isMobile = useIsMobile(768)

  const columns: ColDef[] = [
    {
      headerName: '',
      field: '',
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: 'agGroupCellRenderer',
    },
    { headerName: t('firm_management.table.columns.firm_name'), field: 'name' },
    ...(!isSuperViewer
      ? [
          {
            headerName: '',
            field: 'id',
            width: 50,
            cellRenderer: (props: CustomCellRendererProps) => (
              <IdeyaButton
                ghost
                icon={<EditOutlined />}
                onClick={() => onEditFirm(props.data.id)}
              />
            ),
          },
        ]
      : []),
    { headerName: t('firm_management.table.columns.type'), field: 'type' },
    {
      headerName: t('firm_management.table.columns.state'),
      field: 'isActive',
      cellRenderer: StateCell,
      cellRendererParams: (params: { value: boolean }) => ({
        isActive: Boolean(params.value),
      }),
    },
    {
      headerName: t('firm_management.table.columns.user'),
      field: 'user',
      cellRenderer: UserButtonCell,
      sortable: false,
    },
  ]

  const gridOptions: GridOptions<FirmData> = {
    masterDetail: true,
    isRowMaster: () => true,
    detailCellRenderer: GroupDetailRenderer,
    detailCellRendererParams: (params: { data: { id: string } }) => ({
      groupQueryBase,
      onClick: () => createGroupClicked(params.data.id),
      onEditGroup,
    }),
    detailRowAutoHeight: true,
    getRowId: (p) => p.data.id,
    animateRows: true,
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  )

  return (
    <IdeyaAgGridTable
      columns={columns}
      defaultColDef={defaultColDef}
      rowData={firmList}
      loading={isLoading || isFetching}
      gridOptions={gridOptions}
      height={height}
      autoSizeStrategy={isMobile ? undefined : { type: 'fitGridWidth' }}
      rowSelection={{
        mode: 'multiRow',
        copySelectedRows: true,
      }}
      canCopy={true}
    />
  )
}
